function normalizeApiBaseUrl (url: string): string {
  return url.endsWith('/') ? url : `${url}/`
}

export interface LegacyBridgeTokens {
  accessToken: string
  refreshToken: string
}

const bridgeTokenInFlight = new Map<number, Promise<LegacyBridgeTokens | undefined>>()

async function fetchLegacyBridgeTokens (
  apiBaseUrl: string,
  companyId?: number
): Promise<LegacyBridgeTokens | undefined> {
  const cacheKey = companyId ?? 0
  const inFlight = bridgeTokenInFlight.get(cacheKey)

  if (inFlight) {
    return inFlight
  }

  const request = (async (): Promise<LegacyBridgeTokens | undefined> => {
    try {
      const headers: Record<string, string> = {
        Accept: 'application/json'
      }

      if (companyId) {
        headers['CPlug-Company-Id'] = String(companyId)
      }

      const response = await fetch(`${normalizeApiBaseUrl(apiBaseUrl)}legacy/bridge-token`, {
        method: 'GET',
        credentials: 'include',
        headers
      })

      if (!response.ok) {
        return undefined
      }

      const data = await response.json() as { access_token?: string, refresh_token?: string }
      const accessToken = data.access_token ?? ''
      const refreshToken = data.refresh_token ?? ''

      if (accessToken.length === 0 && refreshToken.length === 0) {
        return undefined
      }

      return { accessToken, refreshToken }
    } catch {
      return undefined
    }
  })()

  bridgeTokenInFlight.set(cacheKey, request)

  try {
    return await request
  } finally {
    bridgeTokenInFlight.delete(cacheKey)
  }
}

async function fetchLegacyBridgeAccessToken (
  apiBaseUrl: string,
  companyId?: number
): Promise<string | undefined> {
  return (await fetchLegacyBridgeTokens(apiBaseUrl, companyId))?.accessToken
}

async function resolveLegacyAccessToken (
  apiBaseUrl: string,
  companyId: number | undefined,
  withCredentials: boolean,
  bearerToken: string
): Promise<string> {
  if (!withCredentials) {
    return bearerToken
  }

  if (!companyId) {
    return ''
  }

  return await fetchLegacyBridgeAccessToken(apiBaseUrl, companyId) ?? ''
}

async function resolveLegacyBridgeTokens (
  apiBaseUrl: string,
  companyId: number | undefined,
  withCredentials: boolean,
  bearerToken: string,
  bearerRefreshToken = ''
): Promise<LegacyBridgeTokens | undefined> {
  if (!withCredentials) {
    return { accessToken: bearerToken, refreshToken: bearerRefreshToken }
  }

  if (!companyId) {
    return undefined
  }

  return await fetchLegacyBridgeTokens(apiBaseUrl, companyId)
}

export function useLegacyBridgeToken (): {
  fetchLegacyBridgeAccessToken: typeof fetchLegacyBridgeAccessToken
  fetchLegacyBridgeTokens: typeof fetchLegacyBridgeTokens
  resolveLegacyAccessToken: typeof resolveLegacyAccessToken
  resolveLegacyBridgeTokens: typeof resolveLegacyBridgeTokens
  } {
  return {
    fetchLegacyBridgeAccessToken,
    fetchLegacyBridgeTokens,
    resolveLegacyAccessToken,
    resolveLegacyBridgeTokens
  }
}
